import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from 'features/auth';
import { brandConfig, getEnvironmentLabel } from '../config/brand';

export function DashboardLayout() {
  const { state: authState, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const envLabel = getEnvironmentLabel();

  const navItems = [
    { name: 'Users', path: '/users' },
    { name: 'Voices', path: '/voices' },
    { name: 'Prompts', path: '/prompts' },
    { name: 'Dictionaries', path: '/dictionaries' },
    { name: 'Words', path: '/words' },
    { name: 'Episodes', path: '/episodes' },
    { name: 'Credentials', path: '/provider-credentials' },
  ];

  const isNavActive = (path: string) => {
    switch (path) {
      case '/users':
        return location.pathname.startsWith('/users');
      case '/voices':
        return location.pathname.startsWith('/voice');
      case '/episodes':
        return location.pathname.startsWith('/episode');
      case '/prompts':
        return location.pathname.startsWith('/prompt');
      case '/dictionaries':
        return location.pathname.startsWith('/dictionaries');
      case '/words':
        return location.pathname.startsWith('/words');
      case '/provider-credentials':
        return location.pathname.startsWith('/provider-credentials');
      default:
        return location.pathname === path;
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      {/* Horizontal Header */}
      <header className="bg-white shadow-md">
        <div className="flex items-center justify-between px-6 py-4">
          {/* Logo + Environment Badge */}
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold" style={{ color: brandConfig.primaryColor }}>
              {brandConfig.brandName}
            </h1>
            {envLabel && (
              <span
                className={`text-xs font-semibold px-2 py-0.5 rounded-full uppercase ${
                  envLabel === 'staging'
                    ? 'bg-amber-100 text-amber-800'
                    : 'bg-blue-100 text-blue-800'
                }`}
              >
                {envLabel}
              </span>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 mx-8">
            <ul className="flex space-x-1">
              {navItems.map((item) => {
                const isActive = isNavActive(item.path);
                return (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      className={() =>
                        `flex items-center px-4 py-2 rounded-md transition-colors ${
                          isActive ? 'bg-gray-100 font-semibold' : 'text-gray-700 hover:bg-gray-50'
                        }`
                      }
                      style={isActive ? { color: brandConfig.activeNavColor } : undefined}
                    >
                      <span>{item.name}</span>
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* User info and logout */}
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-3">
              <span className="text-3xl">👤</span>
              <div className="text-right">
                <p className="text-sm font-semibold text-gray-900 capitalize">
                  {authState.user?.role} Admin
                </p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <div className="container mx-auto p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
